/**
 * Pseudonymized, batched analytics client. Zero dependencies; safe to import in any
 * environment (degrades to a no-op when neither an endpoint nor a network sink exists).
 */
import { pseudonymize } from "./pseudonymize.js";

/** The closed set of analytics event types the collector understands. */
export const ANALYTICS_EVENT_TYPES = [
	"page_view",
	"search",
	"search_no_results",
	"result_click",
	"form_view",
	"form_submit",
	"lead_created",
] as const;

/** Union of valid {@link AnalyticsEvent} `type` values. */
export type AnalyticsEventType = (typeof ANALYTICS_EVENT_TYPES)[number];

/** A trackable event: a `type` plus arbitrary (pseudonymized) properties. */
export interface AnalyticsEvent {
	type: AnalyticsEventType;
	[key: string]: unknown;
}

/** A minimal analytics sink. */
export interface Analytics {
	/** Enqueue an event; sensitive fields are pseudonymized before it is sent. */
	track(event: AnalyticsEvent): void;
}

/** Options for {@link createAnalytics}. */
export interface CreateAnalyticsOptions {
	/** Collector URL. When omitted, events are pseudonymized but never sent. */
	endpoint?: string;
}

interface EnqueuedEvent extends AnalyticsEvent {
	/** Client timestamp (ms). */
	ts: number;
}

/**
 * Create a batched, privacy-first {@link Analytics} sink. Events are queued, flushed
 * on a microtask, pseudonymized, and delivered via `navigator.sendBeacon` (falling
 * back to keepalive `fetch`). Best-effort: transport failures are swallowed.
 *
 * @param options Optional collector endpoint; without it the sink is a no-op transport.
 * @returns An {@link Analytics} instance.
 */
export function createAnalytics(options: CreateAnalyticsOptions = {}): Analytics {
	const endpoint = options.endpoint;
	const queue: EnqueuedEvent[] = [];
	let flushScheduled = false;

	function send(batch: EnqueuedEvent[]): void {
		if (!endpoint || batch.length === 0) return;
		const body = JSON.stringify({ events: batch });

		// Prefer sendBeacon (survives page unload); fall back to keepalive fetch.
		const beacon =
			typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
				? navigator.sendBeacon.bind(navigator)
				: undefined;
		if (beacon) {
			const blob =
				typeof Blob !== "undefined" ? new Blob([body], { type: "application/json" }) : body;
			const ok = beacon(endpoint, blob);
			if (ok) return;
		}

		if (typeof fetch === "function") {
			void fetch(endpoint, {
				method: "POST",
				headers: { "content-type": "application/json" },
				body,
				keepalive: true,
			}).catch(() => {
				/* analytics is best-effort; never surface transport errors */
			});
		}
	}

	function flush(): void {
		flushScheduled = false;
		if (queue.length === 0) return;
		const batch = queue.splice(0, queue.length);
		send(batch);
	}

	function scheduleFlush(): void {
		if (flushScheduled) return;
		flushScheduled = true;
		if (typeof queueMicrotask === "function") {
			queueMicrotask(flush);
		} else {
			setTimeout(flush, 0);
		}
	}

	return {
		track(event: AnalyticsEvent): void {
			const { type, ...rest } = event;
			const safe = pseudonymize(rest);
			queue.push({ type, ...safe, ts: Date.now() });
			scheduleFlush();
		},
	};
}

export { pseudonymize, hashValue, isSensitiveKey } from "./pseudonymize.js";
